// Report and block — the two things a member can do about another member.
//
// Report tells US (a club admin, or super admin) about something in a chat.
// Block protects the member themselves: it stops direct messages both ways and
// hides the blocked person's messages in group chats. The server enforces all
// of that; this composable only calls it and keeps the local copy of "who I've
// blocked" so the UI can show the right button without a request per bubble.
//
// Imported explicitly (house style) — a new file in app/composables/ only
// auto-registers once the dev server rescans, and a missing composable is a
// runtime failure the production build does not catch.
import {ref, computed} from 'vue';
import {endpoints} from '~/network/endpoints.js';
import {useHttpRequest} from '~/composables/useHttpRequest';
import {useMessaging} from '~/composables/useMessaging';

// Reasons a member can pick. Names match the API enum EXACTLY — it is
// serialised by name, so a mismatch silently creates a report with the wrong
// reason rather than failing.
export const REPORT_REASONS = [
    {value: 'Harassment', title: 'Harassment or bullying'},
    {value: 'IllegalContent', title: 'Illegal content'},
    {value: 'ChildSafety', title: 'Concern about a child'},
    {value: 'Spam', title: 'Spam'},
    {value: 'Other', title: 'Something else'},
];

export const REVIEW_STATUS = {open: 'Open', actioned: 'Actioned', dismissed: 'Dismissed'};

/** The first line of the API's {global:[...]} body, or a fallback. */
export function apiMessage(e, fallback) {
    return e?.global?.[0] || e?.data?.global?.[0] || fallback;
}

export function useSafety() {
    const {httpRequest} = useHttpRequest();
    const {syncMessages} = useMessaging();

    // Shared across every component so a block in one place updates every bubble.
    const blockedUserIds = useState('safetyBlockedUserIds', () => null); // null = not loaded

    const loadBlocked = async ({force = false} = {}) => {
        if (blockedUserIds.value !== null && !force) return blockedUserIds.value;
        try {
            const list = await httpRequest(endpoints.userBlocks.list);
            blockedUserIds.value = (list || []).map(b => b.userId);
        } catch {
            blockedUserIds.value = blockedUserIds.value || [];
        }
        return blockedUserIds.value;
    };

    const isBlocked = (userId) => (blockedUserIds.value || []).includes(userId);

    const block = async (userId) => {
        await httpRequest(endpoints.userBlocks.block(userId));
        blockedUserIds.value = [...new Set([...(blockedUserIds.value || []), userId])];
    };

    const unblock = async (userId) => {
        await httpRequest(endpoints.userBlocks.unblock(userId));
        blockedUserIds.value = (blockedUserIds.value || []).filter(id => id !== userId);
        // What the block withheld never reached this browser. The server marks
        // those messages as changed on unblock, so a sync brings them back.
        syncMessages().catch(() => {});
    };

    const listBlocked = () => httpRequest(endpoints.userBlocks.list);

    /**
     * Reports one message. A whole conversation can't be reported: the API
     * refuses a report with no message (owner decision, 25 Sep 2026).
     */
    const report = ({conversationId, conversationMessageId, reason, details}) =>
        httpRequest(endpoints.contentReports.create, {conversationId, conversationMessageId, reason, details});

    // ── the review queue (club admin / super admin) ──
    const listReports = (status) =>
        httpRequest({...endpoints.contentReports.list, path: endpoints.contentReports.list.path + (status ? `?status=${status}` : '')});
    const reportSummary = () => httpRequest(endpoints.contentReports.summary);
    const review = (id, {status, reviewNotes, removeContent}) =>
        httpRequest(endpoints.contentReports.review(id), {status, reviewNotes, removeContent});

    return {
        blockedUserIds: computed(() => blockedUserIds.value || []),
        loadBlocked, isBlocked, block, unblock, listBlocked,
        report, listReports, reportSummary, review,
    };
}
